import React, { useState, useEffect, useRef, useCallback, useMemo, useContext } from 'react';
import { QuestionnaireIdPrefixContext } from './QuestionnaireIdPrefixContext';
import { X, Camera, Upload, SwitchCameraIcon, AlertCircle, Edit3, Save } from 'lucide-react';
import { MoldOrder, EntityType } from '../../types/mold';
import useFileUpload from '../../hooks/useFileUpload';
import { FileResponse, ImageRecord } from '../../types/moldOrder';
import { fileService } from '../../services/api';
import { isFinalStep } from '../MoldDetail/utils';
import Modal from '../shared/Modal';
import Portal from '../shared/Portal';
import PhotoItem from './PhotoItem';
import FashionGallery from './FashionGallery';
import { useFileContext } from '../../contexts/FileContext';
import DeletingModal from '../shared/DeletingModal';
import { FileInfo } from '../../types/upload';
import { DndContext, DragEndEvent, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const MAX_PHOTOS_PER_SECTION = 10;

const SortablePhoto: React.FC<{ id: string; children: React.ReactNode }> = ({ id, children }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    return <div ref={setNodeRef} {...attributes} {...listeners} style={{ transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 10 : undefined, touchAction: 'none' }} className={isDragging ? 'scale-[1.03] cursor-grabbing rounded-lg shadow-xl opacity-85' : 'cursor-grab'}>{children}</div>;
};

const SortablePhotoGrid: React.FC<{
    items: string[];
    onDragEnd: (event: DragEndEvent) => void;
    children: React.ReactNode;
}> = ({ items, onDragEnd, children }) => {
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );
    return <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={items} strategy={rectSortingStrategy}>{children}</SortableContext>
    </DndContext>;
};

const ErrorModal = ({ isOpen, onClose, errorMessage }: { isOpen: boolean; onClose: () => void; errorMessage: string }) => {
    if (!isOpen) return null;
    return (
        <Modal onClose={onClose} maxWidth="max-w-md" isBackgroundTransparent={true}>
            <div className="px-4 py-4 flex flex-col justify-start">
                <div className="flex items-center mb-2">
                    <AlertCircle className="w-6 h-6 text-red-500 mr-2" />
                    <h2 className="text-xl font-semibold text-red-600">เกิดข้อผิดพลาด</h2>
                </div>
                <p className="text-lg mb-4 text-gray-700">{errorMessage || 'ขออภัย ไม่สามารถลบไฟล์ได้ กรุณาลองใหม่อีกครั้ง'}</p>
                <div className="text-right">
                    <button onClick={onClose} className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600">ปิด</button>
                </div>
            </div>
        </Modal>
    );
};

const NoteEditModal = ({
    isOpen,
    onClose,
    currentNote,
    onSave,
    fileName,
    isReadOnly = false
}: {
    isOpen: boolean;
    onClose: () => void;
    currentNote: string;
    onSave: (note: string) => void;
    fileName: string;
    isReadOnly?: boolean;
}) => {
    const [note, setNote] = useState(currentNote);

    useEffect(() => {
        setNote(currentNote);
    }, [currentNote]);

    const handleSave = () => {
        onSave(note);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <Modal onClose={onClose} maxWidth="max-w-md md:max-w-xl" isBackgroundTransparent={true}>
            <div className="px-4 py-4 flex flex-col justify-start">
                <div className="flex items-center mb-4">
                    <Edit3 className="w-5 h-5 text-blue-500 mr-2" />
                    <h2 className="text-lg font-semibold text-gray-800">เพิ่มหมายเหตุรูปภาพ</h2>
                </div>
                <div className="mb-3">
                    <p className="text-sm text-gray-600 mb-2">ไฟล์: {fileName}</p>
                    <textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="เพิ่มหมายเหตุสำหรับรูปภาพนี้..."
                        className="w-full h-24 p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        maxLength={255}
                        disabled={isReadOnly}
                    />
                    <div className="text-xs text-gray-500 mt-1">{note.length}/255</div>
                </div>
                <div className="flex justify-end gap-2">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                    >
                        ยกเลิก
                    </button>
                    {!isReadOnly && (
                        <button
                            onClick={handleSave}
                            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-1"
                        >
                            <Save className="w-4 h-4" />
                            บันทึก
                        </button>
                    )}
                </div>
            </div>
        </Modal>
    );
};

const CameraModal = ({ isLoading, setIsCameraOpen, setIsLoading, handleFileChange }: any) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const [cameraError, setCameraError] = useState<string | null>(null);
    const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
    const [selectedCamera, setSelectedCamera] = useState<string | null>(null);
    const [cameraPermission, setCameraPermission] = useState<PermissionStatus | null>(null);
    const [capturedImage, setCapturedImage] = useState<{
        blob: Blob;
        preview: string;
    } | null>(null);

    const toggleDevice = async () => {
        if (!devices.length) return;
        const currentIndex = devices.findIndex((device) => device.deviceId === selectedCamera);
        const nextIndex = (currentIndex + 1) % devices.length;
        const nextCamera = devices[nextIndex];
        setSelectedCamera(nextCamera.deviceId);
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
        }
        try {
            await openCamera(nextCamera.deviceId);
        } catch (err: any) {
            if (err.name === 'NotReadableError') {
                setCameraError('Could not start video source. Please try again or choose another camera.');
            }
        }
    };

    const capturePhoto = useCallback(() => {
        if (videoRef.current) {
            const canvas = document.createElement('canvas');
            canvas.width = videoRef.current.videoWidth;
            canvas.height = videoRef.current.videoHeight;
            const ctx = canvas.getContext('2d');
            if (ctx) {
                ctx.drawImage(videoRef.current, 0, 0);
                canvas.toBlob((blob) => {
                    if (blob) {
                        const preview = URL.createObjectURL(blob);
                        setCapturedImage({ blob, preview });
                    }
                }, 'image/jpeg');
            }
        }
    }, []);

    const openCamera = async (cameraId?: string) => {
        setIsLoading(true);
        setCameraError(null);
        const targetCameraId = cameraId || selectedCamera;
        try {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
            }
            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    deviceId: targetCameraId ? { exact: targetCameraId } : undefined,
                    width: { ideal: 1280 },
                    height: { ideal: 720 }
                }
            });
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
            }
            setIsCameraOpen(true);
        } catch (err: any) {
            setCameraError('ไม่สามารถเข้าถึงกล้องได้ กรุณาตรวจสอบการตั้งค่าและอนุญาตให้แอปพลิเคชันเข้าถึงกล้อง');
            if (err.name === 'NotReadableError') {
                setCameraError('Could not start video source. Please check camera usage.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const closeCamera = useCallback(() => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
        }
        if (capturedImage) {
            URL.revokeObjectURL(capturedImage.preview);
        }
        setCapturedImage(null);
        setIsCameraOpen(false);
    }, [capturedImage, setIsCameraOpen]);

    const handleConfirmPhoto = useCallback(() => {
        if (capturedImage) {
            const file = new File([capturedImage.blob], `camera-capture-${Date.now()}.jpg`, { type: 'image/jpeg' });
            const event = {
                target: {
                    files: [file]
                }
            } as unknown as React.ChangeEvent<HTMLInputElement>;
            handleFileChange(event);
            setCapturedImage(null);
            closeCamera();
            URL.revokeObjectURL(capturedImage.preview);
        }
    }, [capturedImage, handleFileChange, closeCamera]);

    const handleRetakePhoto = useCallback(async () => {
        if (capturedImage) {
            URL.revokeObjectURL(capturedImage.preview);
            setCapturedImage(null);
            openCamera();
        }
    }, [capturedImage, selectedCamera]);

    useEffect(() => {
        const getDevicesWithPermission = async () => {
            try {
                const initialStream = await navigator.mediaDevices.getUserMedia({ video: true });
                const devices = await navigator.mediaDevices.enumerateDevices();
                const videoDevices = devices.filter((device) => device.kind === 'videoinput');
                setDevices(videoDevices);
                if (videoDevices.length > 0) {
                    const backCamera = videoDevices.find(device =>
                        device.label.toLowerCase().includes('back') ||
                        device.label.toLowerCase().includes('rear') ||
                        device.label.toLowerCase().includes('environment')
                    );
                    const preferredCamera = backCamera?.deviceId || videoDevices[0].deviceId;
                    setSelectedCamera(preferredCamera);
                    initialStream.getTracks().forEach(track => track.stop());
                }
            } catch (err) {
                setCameraError('ไม่สามารถเข้าถึงกล้องได้ กรุณาตรวจสอบการตั้งค่าและอนุญาตให้แอปพลิเคชันเข้าถึงกล้อง');
            }
        };
        getDevicesWithPermission();
    }, []);

    useEffect(() => {
        const handlePermissionChange = (e: Event) => {
            setCameraPermission(e.target as PermissionStatus);
        };
        navigator.permissions.query({ name: 'camera' as PermissionName })
            .then(permissionStatus => {
                setCameraPermission(permissionStatus);
                permissionStatus.addEventListener('change', handlePermissionChange);
            });
        return () => {
            cameraPermission?.removeEventListener('change', handlePermissionChange);
        };
    }, []);

    useEffect(() => {
        if (selectedCamera) {
            openCamera();
        }
        return () => {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
            }
            if (videoRef.current) {
                videoRef.current.srcObject = null;
            }
            if (capturedImage) {
                URL.revokeObjectURL(capturedImage.preview);
            }
        };
    }, [selectedCamera]);

    return (
        <Modal onClose={closeCamera} fullScreen={true}>
            <div className="relative h-full bg-black">
                {isLoading && (
                    <div className="absolute inset-0 flex items-center justify-center text-white bg-black bg-opacity-50 z-10 text-2xl">
                        กำลังเปิดกล้อง...
                    </div>
                )}
                {cameraError && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-white bg-black/90 p-4 text-center z-10">
                        <p className="text-red-500 mb-4">{cameraError}</p>
                        {cameraPermission?.state === 'denied' && (
                            <button
                                onClick={() => window.location.reload()}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                                รีเฟรชหน้านี้
                            </button>
                        )}
                    </div>
                )}
                <div className="h-full flex flex-col">
                    {capturedImage ? (
                        <>
                            <img
                                src={capturedImage.preview}
                                alt="Captured"
                                className="w-full h-full object-contain"
                            />
                            <div className="absolute top-0 right-0 p-4">
                                <button
                                    onClick={closeCamera}
                                    className="p-4 rounded-full bg-red-400 text-white hover:bg-red-600 active:bg-white/40"
                                >
                                    <X className="w-8 h-8" />
                                </button>
                            </div>
                            <div className="absolute bottom-0 inset-x-0 pb-8 pt-20 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
                                <div className="flex items-center justify-end gap-2 px-6">
                                    <button
                                        onClick={handleRetakePhoto}
                                        className="text-2xl px-8 py-3 rounded-full bg-white/20 text-white hover:bg-white/30 active:bg-white/40"
                                    >
                                        ถ่ายใหม่
                                    </button>
                                    <button
                                        onClick={handleConfirmPhoto}
                                        className="text-2xl px-10 py-3 rounded-full bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800"
                                    >
                                        ยืนยัน
                                    </button>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <video
                                ref={videoRef}
                                autoPlay
                                playsInline
                                muted
                                className="w-full h-full object-contain"
                            />
                            <div className="absolute top-0 right-0 p-4">
                                <button
                                    onClick={closeCamera}
                                    className="p-4 rounded-full bg-red-400 text-white hover:bg-red-600 active:bg-white/40"
                                >
                                    <X className="w-8 h-8" />
                                </button>
                            </div>
                            <div className="absolute bottom-0 inset-x-0 pb-8 pt-20 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
                                <div className="flex items-center justify-between px-6">
                                    <div className='w-1/3'></div>
                                    <div className='w-1/3 flex justify-center'>
                                        <button
                                            onClick={capturePhoto}
                                            disabled={isLoading || !!cameraError}
                                            className="w-24 h-24 rounded-full bg-white/20 backdrop-blur border-4 border-white flex items-center justify-center hover:bg-white/30 active:bg-white/40 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <div className="w-20 h-20 rounded-full bg-white"></div>
                                        </button>
                                    </div>
                                    <div className='w-1/3 flex justify-end'>
                                        {devices.length > 1 && (
                                            <button
                                                onClick={toggleDevice}
                                                className="p-4 rounded-full bg-white/20 text-white hover:bg-white/30 active:bg-white/40"
                                            >
                                                <SwitchCameraIcon className="w-8 h-8" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </Modal>
    );
};

/**
 * ImagesUploader Component
 * 
 * A flexible component for uploading and managing images for different entity types.
 * 
 * The onFileChange callback provides the parent component with the current complete list
 * of uploaded files, including fileName and tag information, whenever files are added or removed.
 * 
 * Usage Examples:
 * 
 * // For mold_order entity:
 * <ImagesUploader
 *   moldOrder={moldOrder}
 *   entityType="mold_order"
 *   entityId={moldOrder.id}
 *   tag="some_tag"
 *   title="Photos"
 *   displayTitle="Upload Photos"
 *   onFileChange={(currentFiles) => {
 *     // Handle the current files list with fileName and tag information
 *   }}
 * />
 * 
 * // For insert entity:
 * <ImagesUploader
 *   entityType="insert"
 *   entityId={insert.id}
 *   tag="insert_photos"
 *   title="Insert Photos"
 *   displayTitle="Upload Insert Photos"
 *   isReadOnly={false}
 *   onFileChange={(currentFiles) => {
 *     // Handle the current files list with fileName and tag information
 *     setCurrentUploadedFiles(currentFiles);
 *   }}
 * />
 */
interface ImagesUploaderProps {
    key?: string;
    moldOrder?: MoldOrder;
    entityType: EntityType;
    entityId: number;
    tag?: string;
    title: string;
    displayTitle: string;
    displayStyle?: 'default' | 'inline';
    hideTag?: boolean;
    hideButtonWhenComplete?: boolean;
    headerComponent?: React.ReactNode;
    files?: FileResponse[];
    /** Callback that receives the current list of uploaded files with fileName and tag information */
    onFileChange?: (currentFiles: FileInfo[]) => void;
    maxPhotoCount?: number;
    displayTitleAlignment?: 'justify-center' | 'justify-start';
    className?: string;
    isReadOnly?: boolean; // Optional prop to handle read-only mode
    /** Hide remove button */
    disabledRemove?: boolean;
    /** Render restrained add controls instead of two large gallery tiles. */
    compactAddControls?: boolean;
    onPhotoReorder?: (activeFileName: string, overFileName: string) => void;
    coverFileName?: string;
    onSetCover?: (fileName: string) => void;
    /** Lets an owning domain persist captions outside generic file metadata. */
    onPhotoCaptionChange?: (fileName: string, caption: string) => Promise<void> | void;
}

const ImagesUploader: React.FC<ImagesUploaderProps> = ({
    moldOrder,
    entityType,
    entityId,
    tag,
    files: propFiles = [],
    title,
    displayTitle,
    displayStyle = 'default',
    hideTag = false,
    hideButtonWhenComplete = false,
    headerComponent = null,
    onFileChange,
    maxPhotoCount = MAX_PHOTOS_PER_SECTION,
    displayTitleAlignment = "justify-between",
    className = '',
    isReadOnly = false,
    disabledRemove = false,
    compactAddControls = false,
    onPhotoReorder,
    coverFileName,
    onSetCover,
    onPhotoCaptionChange,
}) => {
    const { files: contextFiles, addFile, removeFile } = useFileContext();
    const files = propFiles || contextFiles;
    const [imageMap, setImageMap] = useState<Record<string, ImageRecord>>({});
    const [isDeletingLocal, setIsDeletingLocal] = useState(false);
    const [isCameraOpen, setIsCameraOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showLimitError, setShowLimitError] = useState(false);
    const [currentPhotoIndex, setCurrentPhotoIndex] = useState<number | null>(null);
    const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
    const [errorModalOpen, setErrorModalOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [noteEditIndex, setNoteEditIndex] = useState<number | null>(null);
    const [noteEditModalOpen, setNoteEditModalOpen] = useState(false);
    const [currentPhoto, setCurrentPhoto] = useState<Partial<FileResponse> | null>(null);
    const { uploadFiles, isUploading, uploadError, setUploadError } = useFileUpload();
    // Logic for fetching file previews
    const fetchPreviews = async (fileData: FileResponse[]) => {
        return await Promise.all(fileData.map(async (file) => {
            try {
                const response = await fileService.downloadFile(file.fileName);
                const blobUrl = URL.createObjectURL(response.data);
                return { fileName: file.fileName, blobUrl, error: false };
            } catch (error) {
                console.log('Error fetching blob for file:', file.fileName, error);
                return { fileName: file.fileName, blobUrl: '', error: true };
            }
        }));
    };

    // Memoize filtered files to prevent unnecessary re-calculations
    const filteredFiles = useMemo(() => {
        return files.filter(file => !tag || file.tag === tag);
    }, [files, tag]);

    useEffect(() => {
        const fetchInitialImages = async () => {
            if (filteredFiles.length === 0) {
                setImageMap({});
                return;
            }

            // Only fetch previews for files that aren't already in imageMap or are still loading
            const filesToFetch = filteredFiles.filter((file: FileResponse) =>
                !imageMap[file.fileName] || imageMap[file.fileName].loading
            );

            if (filesToFetch.length === 0) {
                // Keep the visual map in the order supplied by the owner. This
                // is what makes a completed drag reorder appear immediately.
                setImageMap(prev => Object.fromEntries(
                    filteredFiles.map(file => [file.fileName, prev[file.fileName]]).filter(([, image]) => Boolean(image))
                ));
                return;
            }

            // Initialize loading state for new files only
            const newLoadingEntries = filesToFetch.reduce((acc: Record<string, ImageRecord>, file: FileResponse) => {
                acc[file.fileName] = {
                    fileName: file.fileName,
                    blob: '',
                    loading: true,
                    error: false,
                    notes: file.notes || '' // Include notes from file data, default to empty string for backward compatibility
                };
                return acc;
            }, {});

            setImageMap(prev => ({ ...prev, ...newLoadingEntries }));

            // Fetch previews only for new files
            const previews = await fetchPreviews(filesToFetch);
            const updatedEntries = previews.reduce((acc: Record<string, ImageRecord>, { fileName, blobUrl, error }) => {
                // Find the corresponding file to get notes
                const fileData = filesToFetch.find(f => f.fileName === fileName);
                acc[fileName] = {
                    fileName,
                    blob: blobUrl,
                    loading: false,
                    error,
                    notes: fileData?.notes || '' // Include notes, default to empty string for backward compatibility
                };
                return acc;
            }, {});

            setImageMap(prev => ({ ...prev, ...updatedEntries }));
        };
        fetchInitialImages();
    }, [filteredFiles]); // Use memoized filteredFiles instead of files and tag

    const resetErrorState = () => {
        if (uploadError) setUploadError(null);
    }

    // Upload logic
    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        resetErrorState();
        const newFiles = Array.from(e.target.files || []);
        const imageCount = Object.values(imageMap).length;

        if (imageCount + newFiles.length > maxPhotoCount) {
            setShowLimitError(true);
            setTimeout(() => setShowLimitError(false), 3000);
            return;
        }

        const result = await uploadFiles(entityType, entityId, newFiles, newFiles.reduce((acc, file) => {
            acc[file.name] = { notes: '' };
            if (tag !== undefined) acc[file.name].tag = tag;
            return acc;
        }, {} as Record<string, any>));

        if (result?.data && result.data.length > 0) {
            // Filter out failed uploads
            const successfulUploads = result.data.filter(uploaded =>
                uploaded.fileName && !uploaded.error
            );

            if (successfulUploads.length === 0) {
                return;
            }

            // Map server fileName to the original File object for preview and fetch blob data
            const newPreviews = await Promise.all(successfulUploads.map(async (uploaded) => {
                try {
                    // Add new file to context with complete FileResponse properties
                    // Create a FileResponse object from the UploadFileStatus


                    // Fetch the blob data for the uploaded file
                    const response = await fileService.downloadFile(uploaded.fileName);
                    const blobUrl = URL.createObjectURL(response.data);

                    const fileResponse: FileResponse = {
                        fileName: uploaded.fileName,
                        originalFileName: uploaded.originalFileName || uploaded.fileName,
                        fileSize: 0, // We don't have this information from the upload response
                        id: 0, // We don't have this information from the upload response
                        previewUrl: '',
                        tag: tag,
                        uploadedAt: new Date().toISOString(),
                        uploadedBy: 0,
                        notes: '', // Initialize with empty note for new uploads
                    };
                    addFile(fileResponse);

                    return { fileName: uploaded.fileName, blobUrl, error: false };
                } catch (error) {
                    console.error('Error fetching blob for uploaded file:', uploaded.fileName, error);
                    return { fileName: uploaded.fileName, blobUrl: '', error: true };
                }
            }));

            const newImageMapEntries = newPreviews.reduce<Record<string, ImageRecord>>((acc, { fileName, blobUrl, error }) => {
                acc[fileName] = {
                    fileName,
                    blob: blobUrl,
                    loading: false,
                    error,
                    notes: '' // Initialize with empty note for new uploads
                };
                return acc;
            }, {});

            setImageMap(prevImageMap => ({ ...prevImageMap, ...newImageMapEntries }));

            // Call onFileChange with the complete current list of files
            if (onFileChange) {
                // Calculate current files including the newly uploaded ones
                const existingFiles = files.filter(file => !tag || file.tag === tag);
                const newlyUploadedFiles = successfulUploads.map(uploadStatus => {
                    return {
                        fileName: uploadStatus.fileName!,
                        tag: tag
                    };
                });
                const updatedCurrentFiles = [
                    ...existingFiles.map(file => ({ fileName: file.fileName, tag: file.tag })),
                    ...newlyUploadedFiles
                ];
                onFileChange(updatedCurrentFiles);
            }
        } else {
            // console.log('Upload failed or no data returned:', result);
        }
    };

    // Delete logic
    const handleRemove = async (index: number) => {
        const fileNameToRemove = Object.keys(imageMap)[index];
        if (!fileNameToRemove) return;

        setIsDeletingLocal(true);
        try {
            const result = await fileService.deleteFile(fileNameToRemove);
            if (result.isSuccess) {
                setImageMap(prevMap => {
                    const updatedMap = { ...prevMap };
                    delete updatedMap[fileNameToRemove];
                    return updatedMap;
                });

                // Update context
                removeFile(fileNameToRemove);

                if (uploadError) setUploadError(null);
                setDeleteIndex(null);

                // Call onFileChange with the updated current list of files
                if (onFileChange) {
                    // Calculate current files excluding the removed one
                    const updatedCurrentFiles = files
                        .filter(file => file.fileName !== fileNameToRemove)
                        .filter(file => !tag || file.tag === tag)
                        .map(file => ({ fileName: file.fileName, tag: file.tag }));
                    onFileChange(updatedCurrentFiles);
                }
            } else {
                setErrorMessage(result.errorMessage || 'เกิดข้อผิดพลาดในการลบไฟล์ กรุณาลองใหม่อีกครั้ง');
                setErrorModalOpen(true);
            }
        } catch (err) {
            setErrorMessage('เกิดข้อผิดพลาดในการลบไฟล์ กรุณาลองใหม่อีกครั้ง');
            setErrorModalOpen(true);
        } finally {
            setIsDeletingLocal(false);
        }
    };

    // Note handling functions
    const handleNoteEdit = (index: number) => {
        setNoteEditIndex(index);
        setNoteEditModalOpen(true);
        setCurrentPhoto(imageMap[Object.keys(imageMap)[index]]);
    };

    const handleNoteSave = async (notes: string) => {
        if (noteEditIndex === null) return;

        const fileName = Object.keys(imageMap)[noteEditIndex];
        if (!fileName) return;

        try {
            if (onPhotoCaptionChange) {
                await onPhotoCaptionChange(fileName, notes);
            } else {
                await fileService.updateMetadataFile(fileName, {
                    tag: tag || '',
                    notes: notes
                });
            }

            setImageMap(prev => ({
                ...prev,
                [fileName]: {
                    ...prev[fileName],
                    notes: notes
                }
            }));

            // Update context files if possible
            const fileIndex = files.findIndex(f => f.fileName === fileName);
            if (fileIndex !== -1) {
                const updatedFile = { ...files[fileIndex], notes: notes };
                // Note: For backward compatibility, we'll update the file if possible
                removeFile(fileName);
                addFile(updatedFile);
            }
        } catch (err) {
            console.error('ImagesUploader error happened while updating metadata:', err);
            setErrorMessage('เกิดข้อผิดพลาดในการเพิ่มหมายเหตุรูป กรุณาลองใหม่อีกครั้ง');
            setErrorModalOpen(true);
            return;
        }
    };

    const closeNoteModal = () => {
        setNoteEditIndex(null);
        setNoteEditModalOpen(false);
    };

    // UI logic
    // Per-instance prefix (see QuestionnaireIdPrefixContext) so the file-input id +
    // its label htmlFor don't collide with a second co-mounted Questionnaire — a label
    // click in the PDF-preview overlay would otherwise open the Preview tab's file dialog.
    const idPrefix = useContext(QuestionnaireIdPrefixContext);
    const inputId = `${idPrefix}file-upload-${title}`;
    const imageCount = Object.values(imageMap).length;
    const maxPhotosExceeded = imageCount >= maxPhotoCount;
    const imageListStyle = displayStyle === 'default' ? 'grid grid-cols-2 md:grid-cols-4 gap-4' : 'flex gap-4 overflow-x-auto';
    const disabledAddOrRemove = (moldOrder && isFinalStep(moldOrder.status)) || isReadOnly;

    const handlePhotoClick = (index: number) => setCurrentPhotoIndex(index);
    const closeModal = () => setCurrentPhotoIndex(null);

    const handleCameraClick = () => {
        if (imageCount >= maxPhotoCount) {
            setShowLimitError(true);
            setTimeout(() => setShowLimitError(false), 3000);
            return;
        }
        setIsCameraOpen(true);
    };

    const cancelDeleteIntention = () => {
        setDeleteIndex(null);
    };

    const hideUploadButton = hideButtonWhenComplete && imageCount >= maxPhotoCount;
    const showUploadButton = !disabledAddOrRemove && !hideUploadButton

    const handlePhotoDragEnd = (event: DragEndEvent) => {
        if (!onPhotoReorder || !event.over || event.active.id === event.over.id) return;
        onPhotoReorder(String(event.active.id), String(event.over.id));
    };

    const imageGrid = (
        <div className={`${imageListStyle} ${className}`}>
            {Object.values(imageMap).map((imageRecord: ImageRecord, index) => {
                const photo = <PhotoItem
                    blobFileName={imageRecord.blob}
                    index={index}
                    title={title}
                    isLoading={imageRecord.loading}
                    hasError={imageRecord.error}
                    disabledAddOrRemove={disabledAddOrRemove}
                    fileName={imageRecord.fileName}
                    notes={imageRecord.notes}
                    onPhotoClick={handlePhotoClick}
                    onDeleteClick={(idx) => {
                        setDeleteIndex(idx);
                    }}
                    onNoteEdit={handleNoteEdit}
                    hideTag={hideTag}
                    isDeleting={isDeletingLocal && deleteIndex === index}
                    disabledRemove={disabledRemove}
                    isCover={coverFileName === imageRecord.fileName}
                    onSetCover={onSetCover ? () => onSetCover(imageRecord.fileName) : undefined}
                    captionMode={Boolean(onPhotoCaptionChange)}
                />;
                return onPhotoReorder
                    ? <SortablePhoto key={`photo-${imageRecord.fileName}`} id={imageRecord.fileName}>{photo}</SortablePhoto>
                    : <React.Fragment key={`photo-${imageRecord.fileName}`}>{photo}</React.Fragment>;
            })}
            {isUploading && (
                <div className="relative aspect-square min-h-32 flex flex-col items-center justify-center bg-gray-100 rounded-lg animate-pulse">
                    <svg className="animate-spin h-10 w-10 text-blue-500 mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                    </svg>
                    <span className="text-blue-600 font-medium">กำลังอัพโหลด...</span>
                </div>
            )}
            {showUploadButton && !compactAddControls && (
                <div className={`${showUploadButton ? 'flex-1' : ''} aspect-square flex flex-col gap-2 ${maxPhotosExceeded ? 'opacity-50 cursor-not-allowed' : ''}`}>
                    <label htmlFor={inputId} className={`flex-1 ${maxPhotosExceeded ? 'pointer-events-none' : ''}`}>
                        <div className="h-full border-2 border-dashed border-gray-300 hover:border-blue-600 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 text-gray-400 hover:text-blue-600">
                            <Upload className="h-6 w-6" />
                            <span>อัพโหลดรูป</span>
                        </div>
                        <input
                            id={inputId}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleFileChange}
                            multiple
                            disabled={maxPhotosExceeded}
                        />
                    </label>
                    <button
                        onClick={handleCameraClick}
                        disabled={maxPhotosExceeded}
                        className="flex-1 border-2 border-dashed border-gray-300 hover:border-blue-600 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 text-gray-400 hover:text-blue-600 disabled:hover:border-gray-300 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                    >
                        <Camera className="h-6 w-6" />
                        <span>ถ่ายรูป</span>
                    </button>
                </div>
            )}
        </div>
    );

    return (
        <div className="w-full">
            {headerComponent ? headerComponent : displayStyle === 'default' && (
                <div className={`flex ${displayTitleAlignment} gap-2 items-center mb-2 sm:mb-4`}>
                    <h2 className="text-xl font-semibold">{displayTitle}</h2>
                    <span className={`text-sm ${maxPhotosExceeded ? 'text-red-500' : 'text-blue-600'}`}>{imageCount}/{maxPhotoCount}</span>
                </div>
            )}
            {showLimitError && (
                <div className="mb-4 text-center text-red-500 bg-red-50 py-2 rounded-lg">
                    ไม่สามารถเพิ่มรูปได้มากกว่า {maxPhotoCount} รูป
                </div>
            )}
            {onPhotoReorder ? (
                <SortablePhotoGrid items={Object.values(imageMap).map((image) => image.fileName)} onDragEnd={handlePhotoDragEnd}>
                    {imageGrid}
                </SortablePhotoGrid>
            ) : imageGrid}
            {showUploadButton && compactAddControls && (
                <div className="mt-4 flex flex-wrap gap-2">
                    <label htmlFor={inputId} className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100">
                        <Upload className="h-4 w-4" />
                        เพิ่มรูปภาพ
                        <input id={inputId} type="file" accept="image/*" className="hidden" onChange={handleFileChange} multiple disabled={maxPhotosExceeded} />
                    </label>
                    <button onClick={handleCameraClick} disabled={maxPhotosExceeded} className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed">
                        <Camera className="h-4 w-4" />
                        ถ่ายรูป
                    </button>
                </div>
            )}
            {currentPhotoIndex !== null && (
                <Portal>
                    <Modal onClose={closeModal} maxWidth="max-w-4xl" isBackgroundTransparent={true}>
                        <FashionGallery
                            images={Object.values(imageMap).map(record => record.blob)}
                            index={currentPhotoIndex}
                            notes={Object.values(imageMap).map(record => record.notes)}
                        />
                    </Modal>
                </Portal>
            )}
            {!disabledAddOrRemove && !isDeletingLocal && deleteIndex !== null && (
                <Modal onClose={cancelDeleteIntention} maxWidth="max-w-sm" isBackgroundTransparent={true}>
                    <div className="px-4 flex flex-col justify-start">
                        <h2 className="text-xl font-semibold mb-2">ยืนยันการลบรูปภาพ</h2>
                        <p className="text-lg mb-4">คุณต้องการลบรูปภาพนี้หรือไม่?</p>
                        <div className="text-lg flex justify-end gap-2">
                            <button onClick={cancelDeleteIntention} className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400">ยกเลิก</button>
                            <button onClick={() => handleRemove(deleteIndex!)} className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600">ลบ</button>
                        </div>
                    </div>
                </Modal>
            )}
            {isDeletingLocal && deleteIndex !== null && (
                <DeletingModal />
            )}
            {isCameraOpen && (
                <Portal>
                    <CameraModal
                        isLoading={isLoading}
                        setIsCameraOpen={setIsCameraOpen}
                        setIsLoading={setIsLoading}
                        handleFileChange={handleFileChange}
                    />
                </Portal>
            )}
            {uploadError && <ErrorModal
                isOpen={uploadError !== null}
                onClose={() => setUploadError(null)}
                errorMessage={uploadError || ''}
            />}
            {errorModalOpen && (
                <ErrorModal
                    isOpen={errorModalOpen}
                    onClose={() => setErrorModalOpen(false)}
                    errorMessage={errorMessage || ''}
                />
            )}
            {noteEditModalOpen && noteEditIndex !== null && (
                <NoteEditModal
                    isOpen={noteEditModalOpen}
                    onClose={closeNoteModal}
                    currentNote={currentPhoto?.notes || ''}
                    onSave={handleNoteSave}
                    fileName={currentPhoto?.fileName || ''}
                    isReadOnly={isReadOnly}
                />
            )}
        </div>
    );
};

export default ImagesUploader;
